import { configureStore, ThunkAction, Action } from "@reduxjs/toolkit";
import appReducer from "./slices/appSlice/AppSlice";
import {
  persistReducer,
  createMigrate,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  persistStore,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import { combineReducers } from "redux";
import popupReducer from "./slices/popupSlice/popupSlice";
import userReducer from "./slices/userSlice/UserSlice";
import flightsReducer from "./slices/flightsSlice/FlightsSlice";

const persistConfig = {
  key: "root",
  version: 1,
  storage,
  whitelist: [],
};

const appStorePersistConfig = {
  key: "app",
  version: 5,
  storage,
  whitelist: [
    "sessionId",
    "accessToken",
    "refreshToken",
    "userToken",
    "customerIdEncrypted",
    "impactFeAffiliateId",
    "showCvvStep",
    "userIp",
  ],
};
const userMigrations = {
  2: (state: any) => {
    const { permissionErrorModal, eligibilityErrorModal, ...rest } = state;
    return rest;
  },
};

const userStorePersistConfig = {
  key: "user",
  version: 2,
  storage,
  whitelist: [
    "isAuthenticated",
    "userName",
    "personalId",
    "remainingUsageAmount",
    "payCardActivationExpiryTime",
    "transferDisclaimerShown",
    "flightsScreenSaveTsClickId", // persist so a browser refresh doesn't lose the click id before save-ts-flight uses it
    "clickIdByWebsite", // persist so extend-ts-click-timer has feClickId after refresh
  ],
  migrate: createMigrate(userMigrations as any, { debug: false }),
};

const reducer = combineReducers({
  app: persistReducer(appStorePersistConfig, appReducer),
  popup: popupReducer,
  user: persistReducer(userStorePersistConfig, userReducer),
  flights: flightsReducer,
});

const persistedReducer = persistReducer(persistConfig, reducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        // Popup slice stores callbacks (onClose, onConfirm, etc.) by design
        ignoredActionPaths: [
          "payload.onClose",
          "payload.onConfirm",
          "payload.onCancel",
          "payload.buttons",
          "payload.data",
          "payload.config.onClose",
          "payload.config.onConfirm",
          "payload.config.onCancel",
          "payload.config.buttons",
          "payload.config.data",
        ],
        ignoredPaths: ["popup.popups", "popup.currentPopup"],
      },
    }),
});

export const persistor = persistStore(store);

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;
